'use client';
import { useMemo, useState } from 'react';
import { useParams } from 'next/navigation';

import { useIsMobile, useLiveSportsSocket, useMarketTradeModal } from '@/hooks';
import { useEventBookmarkAction } from '@/hooks/shared/useEventBookmarkAction';
import { useSportsById } from '@/services/query/sports.queries';
import { EventMarket } from '@/types';
import {
  createMarketFromGame,
  findMarketByLine,
  getMarketType,
  getOutcomes,
  sortMarketTabs,
} from '@/utils';

import { ROUTES, TRADE_SIDES } from '@/constants';

export const useSportsDetail = () => {
  const params = useParams();
  const eventSlug = params?.id as string;
  const sportSlug = (params?.slug as string)?.toLowerCase() || 'live';

  const { data: rawEvent, isLoading: isEventInitialLoading } = useSportsById(eventSlug);
  const { liveData } = useLiveSportsSocket();

  const sportEvent = useMemo(() => {
    if (!rawEvent || !rawEvent.gameId) return rawEvent;
    const socketUpdate = liveData[rawEvent.gameId.toString()];
    if (!socketUpdate) return rawEvent;

    return {
      ...rawEvent,
      score: socketUpdate.score || rawEvent.score,
      period: socketUpdate.period || rawEvent.period,
      elapsed: socketUpdate.elapsed || rawEvent.elapsed,
      isLive: socketUpdate.live ?? rawEvent.isLive,
    };
  }, [rawEvent, liveData]);

  const { tradeModalState, handleOpenTradeModal, handleCloseTradeModal } = useMarketTradeModal();

  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState('');
  const [sidebarTab, setSidebarTab] = useState<'buy' | 'sell'>('buy');
  const [selectedSide, setSelectedSide] = useState<'yes' | 'no'>(TRADE_SIDES.YES);
  const [selectedMarket, setSelectedMarket] = useState<EventMarket | null>(null);
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<string | null>(null);
  const [selectedMarketType, setSelectedMarketType] = useState<string | null>(null);
  const [selectedOutcomeIndex, setSelectedOutcomeIndex] = useState<number | null>(null);

  const isBookmarked = sportEvent ? checkIsBookmarked(sportEvent.id) : false;

  const tradeSidebarData = useMemo(() => {
    let marketToUse: EventMarket | undefined = undefined;
    if (selectedMarket && sportEvent?.markets) {
      marketToUse = sportEvent.markets.find((m) => String(m.id) === String(selectedMarket.id));
    }
    if (!marketToUse) {
      marketToUse = selectedMarket || undefined;
    }

    if (!marketToUse) {
      marketToUse =
        sportEvent?.markets?.find((m) => m.sportsMarketType?.toLowerCase() === 'moneyline') ||
        sportEvent?.markets?.[0]; // Fallback to first available market
    }

    if (!sportEvent || !marketToUse) return null;

    const outcomes = getOutcomes(marketToUse);
    const outcomeToUse =
      selectedOutcomeIndex !== null
        ? outcomes[selectedOutcomeIndex] || outcomes[0]
        : selectedOutcomeId
          ? outcomes.find((o) => o.id === selectedOutcomeId) || outcomes[0]
          : outcomes[0];

    if (!outcomeToUse) return null;

    const mappedMarket: any = {
      ...marketToUse,
      title: marketToUse.question || marketToUse.groupItemTitle,
      outcomes: outcomes.map((o) => ({
        ...o,
        price: typeof o.price === 'string' ? parseFloat(o.price) : o.price || 0,
      })),
    };

    const mappedOutcome: any = {
      ...outcomeToUse,
      // Keep trade sidebar/modal aligned with cards: prefer orderbook ask.
      price:
        typeof outcomeToUse.bestAsk === 'number'
          ? outcomeToUse.bestAsk
          : typeof outcomeToUse.price === 'string'
            ? parseFloat(outcomeToUse.price)
            : outcomeToUse.price || 0,
    };

    return {
      market: mappedMarket,
      outcome: mappedOutcome,
    };
  }, [sportEvent, selectedMarket, selectedOutcomeIndex, selectedOutcomeId]);

  const tabs = (() => {
    if (sportEvent?.uiPattern === 'single-event-flat') return [];
    const rawLabels = (sportEvent?.marketGroups || []).map((g) => g.label);
    return sortMarketTabs(rawLabels);
  })();

  // Initialize active tab from API data if not already set
  const apiActiveTab = sportEvent?.marketGroups?.find((g) => g.isCurrentTab)?.label;
  const effectiveActiveTab = activeTab || apiActiveTab || (tabs.length > 0 ? tabs[0] : '');

  const handleTeamClick = (teamIndex: 1 | 2) => {
    if (!sportEvent) return;
    const data = createMarketFromGame(sportEvent, teamIndex, sportSlug);
    handleOpenTradeModal(data.market, data.outcome, true, { teamColor: data.teamColor });
  };

  const handleTradeClick = (
    marketId: string,
    outcomeId: string,
    marketType: string,
    isBuy: boolean,
    shouldOpenModal: boolean = true,
  ) => {
    const market = sportEvent?.markets?.find((m) => String(m.id) === String(marketId));
    if (market) {
      setSelectedMarket(market);
      const type = getMarketType(market);
      setSelectedMarketType(type);
      const outcomes = getOutcomes(market);
      const idx = outcomes.findIndex((o) => o.id === outcomeId);
      const foundIdx = idx !== -1 ? idx : 0;
      setSelectedOutcomeIndex(foundIdx);
      setSelectedSide(foundIdx === 1 ? 'no' : 'yes');
    }
    setSelectedOutcomeId(outcomeId);
    setSidebarTab(isBuy ? 'buy' : 'sell');

    if (isMobile && market && shouldOpenModal) {
      const outcomes = getOutcomes(market);
      const outcome = outcomes.find((o) => o.id === outcomeId) || outcomes[0];
      if (outcome) {
        handleOpenTradeModal(market, outcome, isBuy);
      }
    }
  };

  const handleBookmarkClick = (e?: React.MouseEvent) => {
    if (!sportEvent) return;
    handleBookmarkToggle(
      e,
      sportEvent as any,
      'sport',
      ROUTES.GAME_DETAIL(sportSlug, sportEvent.id),
    );
  };

  const handleLineChange = (line: string | number, marketType: string, marketId?: string) => {
    if (!sportEvent?.markets) return;
    const newMarket = marketId
      ? sportEvent.markets.find((m) => String(m.id) === String(marketId))
      : findMarketByLine(sportEvent as any, marketType, line);
    if (newMarket) {
      setSelectedMarket(newMarket as EventMarket);
      setSelectedMarketType(marketType);
    }
  };

  const liveTradeModalState = useMemo(() => {
    if (!tradeModalState.market || !tradeModalState.outcome || !sportEvent?.markets) {
      return tradeModalState;
    }
    const updatedMarket = sportEvent.markets.find(
      (m) => String(m.id) === String(tradeModalState.market?.id),
    );
    if (!updatedMarket) return tradeModalState;

    const outcomes = getOutcomes(updatedMarket);
    const updatedOutcome = outcomes.find(
      (o) => String(o.id) === String(tradeModalState.outcome?.id),
    );
    if (!updatedOutcome) {
      return {
        ...tradeModalState,
        market: updatedMarket,
      };
    }

    return {
      ...tradeModalState,
      market: updatedMarket,
      outcome: updatedOutcome,
    };
  }, [tradeModalState, sportEvent]);

  return {
    state: {
      isEventInitialLoading,
      sportEvent,
      tradeModalState: liveTradeModalState,
      isBookmarked,
      tradeSidebarData,
      selectedSide,
      activeTab: effectiveActiveTab,
      sidebarTab,
      tabs,
      activeOutcomeId: selectedOutcomeId || tradeSidebarData?.outcome?.id || null,
      selectedMarketType,
      selectedOutcomeIndex,
      isPreviousMarket: sportSlug === 'previous',
      uiPattern: sportEvent?.uiPattern,
      relatedTabs: sportEvent?.relatedTabs,
    },
    handlers: {
      handleTeamClick,
      handleBookmarkClick,
      handleCloseTradeModal,
      setSelectedSide,
      setActiveTab,
      setSidebarTab,
      handleTradeClick,
      handleLineChange,
    },
  };
};
